// Production builds fall back to the site's own origin (nginx proxies /api and /uploads).
const BASE_URL =
	process.env.REACT_APP_API_URL ||
	(process.env.NODE_ENV === "production" ? "" : "http://localhost:8080");

function getToken() {
	return localStorage.getItem("admin_token");
}

// Simple in-memory TTL cache for public GET requests
const _cache = new Map();
function cachedGet(path, ttlMs) {
	const now = Date.now();
	const hit = _cache.get(path);
	if (hit && hit.expires > now) return Promise.resolve(hit.data);
	return request(path).then((data) => {
		_cache.set(path, { data, expires: now + ttlMs });
		return data;
	});
}
export function bustCache(prefix) {
	for (const key of _cache.keys()) {
		if (key.startsWith(prefix)) _cache.delete(key);
	}
}

async function request(path, options = {}) {
	const token = getToken();
	const headers = {
		"Content-Type": "application/json",
		...(token ? { Authorization: `Bearer ${token}` } : {}),
		...options.headers,
	};

	const res = await fetch(`${BASE_URL}${path}`, { ...options, headers });

	if (!res.ok) {
		const err = await res.json().catch(() => ({ error: res.statusText }));
		throw new Error(err.error || `HTTP ${res.status}`);
	}

	if (res.status === 204) return null;
	return res.json();
}

// Auth
export const authApi = {
	login: (username, password) =>
		request("/api/auth/login", {
			method: "POST",
			body: JSON.stringify({ username, password }),
		}),
	me: () => request("/api/auth/me"),
	changePassword: (current_password, new_password) =>
		request("/api/auth/change-password", {
			method: "PUT",
			body: JSON.stringify({ current_password, new_password }),
		}),
};

// Content
export const contentApi = {
	listPages: () => request("/api/content/pages"),
	getPage: (slug) => request(`/api/content/pages/${slug}`),
	updatePage: (slug, data) =>
		request(`/api/content/pages/${slug}`, {
			method: "PUT",
			body: JSON.stringify(data),
		}),
	createSection: (data) =>
		request("/api/content/sections", {
			method: "POST",
			body: JSON.stringify(data),
		}),
	updateSection: (id, data) =>
		request(`/api/content/sections/${id}`, {
			method: "PUT",
			body: JSON.stringify(data),
		}),
	deleteSection: (id) =>
		request(`/api/content/sections/${id}`, { method: "DELETE" }),
};

// Blog (public) — list cached for 60 s, individual posts for 5 min
export const blogApi = {
	listPosts: (params = {}) => {
		const qs = new URLSearchParams(
			Object.entries(params).filter(([, v]) => v != null)
		).toString();
		const path = `/api/blog/posts${qs ? `?${qs}` : ""}`;
		return cachedGet(path, 60_000);
	},
	getPost: (slug) => cachedGet(`/api/blog/posts/${slug}`, 300_000),
};

// Blog (admin) — all mutations under /api/admin/blog/
export const blogAdminApi = {
	listAll: (params = {}) => {
		const qs = new URLSearchParams(
			Object.entries(params).filter(([, v]) => v != null)
		).toString();
		return request(`/api/admin/blog/posts${qs ? `?${qs}` : ""}`);
	},
	getById: (id) => request(`/api/admin/blog/posts/${id}`),
	create: (data) =>
		request("/api/admin/blog/posts", {
			method: "POST",
			body: JSON.stringify(data),
		}),
	update: (id, data) =>
		request(`/api/admin/blog/posts/${id}`, {
			method: "PUT",
			body: JSON.stringify(data),
		}),
	delete: (id) =>
		request(`/api/admin/blog/posts/${id}`, { method: "DELETE" }),
};

// Feeds
export const feedsApi = {
	list: () => request("/api/feeds"),
	create: (data) =>
		request("/api/feeds", { method: "POST", body: JSON.stringify(data) }),
	update: (id, data) =>
		request(`/api/feeds/${id}`, {
			method: "PUT",
			body: JSON.stringify(data),
		}),
	delete: (id) => request(`/api/feeds/${id}`, { method: "DELETE" }),
	sync: (id) => request(`/api/feeds/${id}/sync`, { method: "POST" }),
	syncAll: () => request("/api/feeds/sync-all", { method: "POST" }),
};

// Site settings
export const settingsApi = {
	uploadCv: (file) => {
		const formData = new FormData();
		formData.append("file", file);
		const token = getToken();
		return fetch(`${BASE_URL}/api/settings/cv`, {
			method: "POST",
			headers: token ? { Authorization: `Bearer ${token}` } : {},
			body: formData,
		}).then(async (res) => {
			if (!res.ok) {
				const err = await res.json().catch(() => ({ error: res.statusText }));
				throw new Error(err.error || `HTTP ${res.status}`);
			}
			return res.json();
		});
	},
	cvUrl: () => `${BASE_URL}/uploads/cv.pdf`,
	cvViewUrl: () => `${BASE_URL}/api/settings/cv`,
};

// Media
export const mediaApi = {
	list: () => request("/api/media"),
	upload: (file) => {
		const formData = new FormData();
		formData.append("file", file);
		const token = getToken();
		return fetch(`${BASE_URL}/api/media/upload`, {
			method: "POST",
			headers: token ? { Authorization: `Bearer ${token}` } : {},
			body: formData,
		}).then(async (res) => {
			if (!res.ok) {
				const err = await res.json().catch(() => ({ error: res.statusText }));
				throw new Error(err.error || `HTTP ${res.status}`);
			}
			return res.json();
		});
	},
	delete: (id) => request(`/api/media/${id}`, { method: "DELETE" }),
};
